import React, { useState, useEffect } from "react";
import {
  Drawer,
  TextField,
  Button,
  MenuItem,
  Box,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TuneIcon from "@mui/icons-material/Tune";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckIcon from "@mui/icons-material/Check";
import { FontFamily } from "../theme/fontFamily";
import { fontWeight } from "../theme/fontWeight";
import { fontSize } from "../theme/fontSize";
import { ThemeColor } from "../theme/color";
import OutlinedInputField from "../inputfiled/OutlinedInput";
import CustomSelect from "../select field/OutlinedSelectField";

/**
 * `dateLabel` names the date the range applies to. It differs per list - the
 * Applicants list ranges over the entry date, the Payout list over the visa
 * approval date - so the panel must not hardcode one of them.
 */
const FilterComponent = ({
  open,
  onClose,
  onApply,
  fields = [],
  dateLabel = "Entry Date",
  initialFilters = [],
}) => {
  /**
   * Rebuild the panel from what the list actually has applied.
   *
   * Coming back from a student leaves the list filtered, so the panel has to
   * show the same rules - otherwise it reads as "no filters" over a filtered
   * table, which is the confusion this whole change is about, only inverted.
   */
  const splitApplied = (applied) => {
    const rules = [];
    let from = "";
    let to = "";

    (applied || []).forEach((entry) => {
      if (entry.field === "startDate") from = entry.value;
      else if (entry.field === "endDate") to = entry.value;
      else {
        const match = fields.find((f) => f.value === entry.field);
        rules.push({
          field: entry.field,
          value: entry.value,
          type: match?.type,
          option: match?.option,
        });
      }
    });

    return { rules, from, to };
  };

  const restored = splitApplied(initialFilters);

  const [filterFields, setFilterFields] = useState(restored.rules);
  const [fromDate, setFromDate] = useState(restored.from);
  const [toDate, setToDate] = useState(restored.to);
  const [toDateError, setToDateError] = useState("");

  // The options arrive from the page a moment after the first render, so the
  // restored rules are rebuilt once they are there.
  useEffect(() => {
    if (!open) return;
    const next = splitApplied(initialFilters);
    setFilterFields(next.rules);
    setFromDate(next.from);
    setToDate(next.to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formatDate = (date) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  useEffect(() => {
    if (!fromDate || !toDate) return;
    if (new Date(toDate) < new Date(fromDate)) {
      setToDate(fromDate);
      setToDateError("");
    }
  }, [fromDate]);

  const handleAddFilterField = () => {
    setFilterFields([...filterFields, { field: "", value: "", type: "" }]);
  };

  const handleRemoveFilterField = (index) => {
    const updatedFields = filterFields.filter((_, i) => i !== index);
    setFilterFields(updatedFields);
  };

  const handleFieldChange = (index, field) => {
    const updatedFields = [...filterFields];
    updatedFields[index].field = field;
    const match = fields.find((data) => data.value === field);
    updatedFields[index].type = match?.type;
    updatedFields[index].option = match?.option;
    updatedFields[index].value = match?.type === "multi-select" ? [] : "";
    setFilterFields(updatedFields);
  };

  const handleValueChange = (index, value) => {
    const updatedFields = [...filterFields];
    updatedFields[index].value = value;
    setFilterFields(updatedFields);
  };

  const handleDatePreset = (days) => {
    const today = new Date();
    const to = formatDate(today);
    if (days === 0) {
      setFromDate(to);
      setToDate(to);
    } else {
      const from = new Date();
      from.setDate(today.getDate() - days);
      setFromDate(formatDate(from));
      setToDate(to);
    }
    setToDateError("");
  };

  const clearDates = () => {
    setFromDate("");
    setToDate("");
    setToDateError("");
  };

  const applyFilters = () => {
    const finalFilters = [...filterFields];
    if (fromDate) finalFilters.push({ field: "startDate", value: fromDate, type: "date" });
    if (toDate) finalFilters.push({ field: "endDate", value: toDate, type: "date" });

    onApply(finalFilters);
    onClose();
  };

  const resetFilters = () => {
    setFilterFields([]);
    setFromDate("");
    setToDate("");
    setToDateError("");
    onApply([]);
    onClose();
  };

  const activeCount =
    filterFields.filter((f) => f.field && f.value !== "" && (!Array.isArray(f.value) || f.value.length > 0))
      .length +
    (fromDate ? 1 : 0) +
    (toDate ? 1 : 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={300}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          maxWidth: "100vw",
          borderTopLeftRadius: { xs: 0, sm: 20 },
          borderBottomLeftRadius: { xs: 0, sm: 20 },
          bgcolor: "#F8FAFC",
          boxShadow: "-12px 0 35px rgba(0, 0, 0, 0.12)",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        },
      }}
    >
      {/* Sleek Top Header */}
      <Box
        sx={{
          px: 3,
          py: 2.2,
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid #E2E8F0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${ThemeColor.PURPLE} 0%, #2563EB 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#FFFFFF",
              boxShadow: "0 4px 12px rgba(21, 83, 158, 0.25)",
            }}
          >
            <TuneIcon fontSize="small" />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                fontFamily={FontFamily.Poppins}
                fontWeight={600}
                fontSize="1.05rem"
                color="#0F172A"
                lineHeight={1.2}
              >
                Filter Records
              </Typography>
              {activeCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeCount} active`}
                  sx={{
                    height: 20,
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    bgcolor: "#EFF6FF",
                    color: ThemeColor.PURPLE,
                    borderRadius: "6px",
                  }}
                />
              )}
            </Box>
            <Typography fontSize="0.75rem" color="#64748B" sx={{ mt: 0.3 }}>
              Refine list by dates & custom criteria
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#64748B",
            bgcolor: "#F1F5F9",
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: "#E2E8F0",
              color: "#0F172A",
              transform: "rotate(90deg)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Main Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "#CBD5E1",
            borderRadius: 10,
          },
        }}
      >
        {/* Date Range Section Card */}
        <Box
          sx={{
            bgcolor: "#FFFFFF",
            borderRadius: "14px",
            border: "1px solid #E2E8F0",
            p: 2.2,
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.02)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CalendarMonthIcon sx={{ color: ThemeColor.PURPLE, fontSize: 19 }} />
              <Typography
                fontFamily={FontFamily.Poppins}
                fontWeight={600}
                fontSize="0.875rem"
                color="#1E293B"
              >
                Date Range ({dateLabel})
              </Typography>
            </Box>
            {(fromDate || toDate) && (
              <Button
                variant="text"
                size="small"
                onClick={clearDates}
                sx={{
                  fontSize: "0.72rem",
                  py: 0.2,
                  px: 0.8,
                  color: "#EF4444",
                  textTransform: "none",
                  fontWeight: 500,
                  "&:hover": { bgcolor: "#FEE2E2" },
                }}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* Quick Date Presets */}
          <Box sx={{ display: "flex", gap: 0.8, mb: 2, flexWrap: "wrap" }}>
            {[
              { label: "Today", days: 0 },
              { label: "Last 7 Days", days: 7 },
              { label: "Last 30 Days", days: 30 },
            ].map((preset) => (
              <Chip
                key={preset.label}
                label={preset.label}
                size="small"
                clickable
                onClick={() => handleDatePreset(preset.days)}
                sx={{
                  fontSize: "0.75rem",
                  bgcolor: "#F1F5F9",
                  color: "#475569",
                  fontWeight: 500,
                  borderRadius: "6px",
                  "&:hover": {
                    bgcolor: "#E0E7FF",
                    color: ThemeColor.PURPLE,
                  },
                }}
              />
            ))}
          </Box>

          {/* Date Input Fields */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <Box>
              <Typography
                fontSize="0.75rem"
                fontWeight={500}
                color="#64748B"
                sx={{ mb: 0.5 }}
              >
                From Date
              </Typography>
              <TextField
                type="date"
                size="small"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setToDateError("");
                }}
                fullWidth
                inputProps={{
                  max: toDate || undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#F8FAFC",
                    fontSize: "0.85rem",
                    "&:hover fieldset": { borderColor: "#94A3B8" },
                    "&.Mui-focused fieldset": { borderColor: ThemeColor.PURPLE },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                fontSize="0.75rem"
                fontWeight={500}
                color="#64748B"
                sx={{ mb: 0.5 }}
              >
                To Date
              </Typography>
              <TextField
                type="date"
                size="small"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  if (fromDate && e.target.value && new Date(e.target.value) < new Date(fromDate)) {
                    setToDateError("To date cannot be earlier than From date");
                  } else {
                    setToDateError("");
                  }
                }}
                fullWidth
                inputProps={{
                  min: fromDate || undefined,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    bgcolor: "#F8FAFC",
                    fontSize: "0.85rem",
                    "&:hover fieldset": { borderColor: "#94A3B8" },
                    "&.Mui-focused fieldset": { borderColor: ThemeColor.PURPLE },
                  },
                }}
              />
            </Box>
          </Box>

          {toDateError && (
            <Typography color="error" fontSize="0.72rem" sx={{ mt: 1 }}>
              {toDateError}
            </Typography>
          )}
        </Box>

        {/* Filter Conditions Section */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterListIcon sx={{ color: ThemeColor.PURPLE, fontSize: 19 }} />
              <Typography
                fontFamily={FontFamily.Poppins}
                fontWeight={600}
                fontSize="0.875rem"
                color="#1E293B"
              >
                Filter Conditions
              </Typography>
              {filterFields.length > 0 && (
                <Chip
                  size="small"
                  label={filterFields.length}
                  sx={{
                    height: 18,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    bgcolor: "#F1F5F9",
                    color: "#475569",
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Empty State when no filters */}
          {filterFields.length === 0 ? (
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: "14px",
                border: "1.5px dashed #CBD5E1",
                py: 4,
                px: 2,
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  bgcolor: "#EFF6FF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: ThemeColor.PURPLE,
                  mb: 1.2,
                }}
              >
                <FilterListIcon fontSize="small" />
              </Box>
              <Typography
                fontFamily={FontFamily.Poppins}
                fontWeight={600}
                fontSize="0.875rem"
                color="#334155"
              >
                No filter rules added
              </Typography>
              <Typography
                fontSize="0.75rem"
                color="#94A3B8"
                sx={{ maxWidth: 260, mt: 0.5, mb: 2 }}
              >
                Filter by agent, program, degree, application status, unread messages and more.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddFilterField}
                startIcon={<AddIcon />}
                sx={{
                  borderColor: ThemeColor.PURPLE,
                  color: ThemeColor.PURPLE,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.78rem",
                  "&:hover": {
                    bgcolor: "#EFF6FF",
                    borderColor: ThemeColor.PURPLE,
                  },
                }}
              >
                Add First Filter Rule
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {filterFields.map((filter, index) => (
                <Fade in key={index}>
                  <Box
                    sx={{
                      bgcolor: "#FFFFFF",
                      borderRadius: "12px",
                      border: "1px solid #E2E8F0",
                      p: 1.8,
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.02)",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "#93C5FD",
                        boxShadow: "0 4px 12px rgba(21, 83, 158, 0.06)",
                      },
                    }}
                  >
                    {/* Rule Header Bar */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1.2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <Typography
                          fontSize="0.72rem"
                          fontWeight={700}
                          sx={{
                            bgcolor: "#EFF6FF",
                            color: ThemeColor.PURPLE,
                            px: 1,
                            py: 0.2,
                            borderRadius: "5px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          RULE #{index + 1}
                        </Typography>
                        {filter.field && (
                          <Typography
                            fontSize="0.75rem"
                            fontWeight={500}
                            color="#64748B"
                          >
                            {fields.find((f) => f.value === filter.field)?.label}
                          </Typography>
                        )}
                      </Box>

                      <Tooltip title="Remove rule">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFilterField(index)}
                          sx={{
                            color: "#94A3B8",
                            p: 0.5,
                            "&:hover": {
                              color: "#EF4444",
                              bgcolor: "#FEE2E2",
                            },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {/* Rule Inputs: Field Selector + Value Selector */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                      {/* Field Selection */}
                      <TextField
                        select
                        size="small"
                        label="Select Field"
                        value={filter.field}
                        onChange={(e) => handleFieldChange(index, e.target.value)}
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                maxHeight: 320,
                                borderRadius: "12px",
                                border: "1px solid #E2E8F0",
                                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)",
                                "&::-webkit-scrollbar": { width: 5 },
                                "&::-webkit-scrollbar-thumb": {
                                  bgcolor: "#CBD5E1",
                                  borderRadius: 10,
                                },
                                "& .MuiMenuItem-root": {
                                  fontSize: "0.82rem",
                                  py: 1,
                                  px: 1.8,
                                  borderRadius: "6px",
                                  mx: 0.6,
                                  my: 0.2,
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    bgcolor: "#EFF6FF",
                                    color: ThemeColor.PURPLE,
                                  },
                                  "&.Mui-selected": {
                                    bgcolor: "#DBEAFE",
                                    color: "#1E40AF",
                                    fontWeight: 600,
                                    "&:hover": { bgcolor: "#BFDBFE" },
                                  },
                                },
                              },
                            },
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: "#F8FAFC",
                            fontSize: "0.85rem",
                            "&:hover fieldset": { borderColor: "#94A3B8" },
                            "&.Mui-focused fieldset": { borderColor: ThemeColor.PURPLE },
                          },
                        }}
                      >
                        {fields.map((field) => (
                          <MenuItem key={field.value} value={field.value}>
                            {field.label}
                          </MenuItem>
                        ))}
                      </TextField>

                      {/* Value Input based on Field Type */}
                      {filter.field && (
                        <Box sx={{ mt: 0.5 }}>
                          {filter.type === "select" ? (
                            <CustomSelect
                              multiple={false}
                              option={filter?.option || []}
                              value={filter?.value ?? ""}
                              onChange={(e) => handleValueChange(index, e.target.value)}
                              fullWidth={true}
                              label={"Select Value"}
                            />
                          ) : filter.type === "multi-select" ? (
                            <CustomSelect
                              multiple={true}
                              option={filter?.option || []}
                              value={filter?.value === "" || !filter.value ? [] : filter.value}
                              onChange={(e) => handleValueChange(index, e.target.value)}
                              fullWidth={true}
                              label={"Select Values"}
                            />
                          ) : (
                            <OutlinedInputField
                              label="Filter Value"
                              placeholder="Enter value..."
                              value={filter.value}
                              onChange={(e) => handleValueChange(index, e.target.value)}
                              fullWidth
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Fade>
              ))}

              {/* Add another rule button */}
              <Button
                variant="outlined"
                onClick={handleAddFilterField}
                startIcon={<AddIcon />}
                sx={{
                  py: 1.2,
                  borderRadius: "10px",
                  border: "1.5px dashed #CBD5E1",
                  bgcolor: "#FFFFFF",
                  color: ThemeColor.PURPLE,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "#EFF6FF",
                    borderColor: ThemeColor.PURPLE,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                Add Another Condition
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Sticky Bottom Action Bar */}
      <Box
        sx={{
          p: 2.2,
          px: 3,
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 -4px 15px rgba(0, 0, 0, 0.04)",
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={resetFilters}
          startIcon={<RestartAltIcon />}
          sx={{
            flex: 1,
            py: 1.1,
            borderRadius: "9px",
            borderColor: "#E2E8F0",
            color: "#64748B",
            fontWeight: 600,
            fontSize: "0.85rem",
            textTransform: "none",
            "&:hover": {
              borderColor: "#CBD5E1",
              bgcolor: "#F1F5F9",
              color: "#0F172A",
            },
          }}
        >
          Reset
        </Button>

        <Button
          variant="contained"
          onClick={applyFilters}
          startIcon={<CheckIcon />}
          sx={{
            flex: 1.5,
            py: 1.1,
            borderRadius: "9px",
            background: `linear-gradient(135deg, ${ThemeColor.PURPLE} 0%, #1E40AF 100%)`,
            color: "#FFFFFF",
            fontWeight: 600,
            fontSize: "0.85rem",
            textTransform: "none",
            boxShadow: "0 4px 14px rgba(21, 83, 158, 0.35)",
            transition: "all 0.2s ease",
            "&:hover": {
              background: `linear-gradient(135deg, #0F3E78 0%, #1E3A8A 100%)`,
              boxShadow: "0 6px 18px rgba(21, 83, 158, 0.45)",
              transform: "translateY(-1px)",
            },
          }}
        >
          Apply Filters
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterComponent;
